import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, concatMap, exhaustMap, map, switchMap, take, tap } from 'rxjs/operators';
import { of } from 'rxjs';

import {
  getUserSession,
  init,
  initSuccess,
  login,
  loginFailed,
  loginRedirectBilling,
  loginRedirectConfirmation,
  loginSuccess,
  logout,
  logoutSuccess,
  setUserSessionData,
  toggleEmailPreview,
  toggleEmailPreviewSuccess,
  toggleHints,
  toggleHintsSuccess,
  toggleUnsubscribe,
  toggleUnsubscribeSuccess,
  toggleVipFeature,
  toggleVipFeatureSuccess,
} from '../actions/user.action';
import { ConfirmationRedirectError } from 'app/errors/confirmation-redirect.error';
import { BillingRedirectError } from 'app/errors/billing-redirect.error';
import { AccessToken, IUserSessionData } from '../models/user.model';
import { UserService } from '../services/user.service';
import { SessionService } from '../services/session.service';
import { Store } from '@ngrx/store';
import { IReducers } from '../reducers';
import { openSnackbar } from '../actions/page.action';

@Injectable()
export class UserEffects {
  login$ = createEffect(() =>
    this.actions$.pipe(
      ofType(login),
      switchMap(({ username, password }) =>
        this.userService.login({ username, password }).pipe(
          map((accessToken: AccessToken) => loginSuccess({ accessToken })),
          catchError(error => {
            if (error instanceof BillingRedirectError) {
              return of(loginRedirectBilling({ username }));
            } else if (error instanceof ConfirmationRedirectError) {
              return of(loginRedirectConfirmation());
            }

            return of(loginFailed({ error }));
          }),
        ),
      ),
    ),
  );

  loginSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loginSuccess),
      exhaustMap(() => {
        return of(init());
      }),
    ),
  );

  init$ = createEffect(() =>
    this.actions$.pipe(
      ofType(init),
      exhaustMap(() => {
        const token = localStorage.getItem('token');

        if (token) {
          this.sessionService.loadUserSession();
        } else {
          this.sessionService.destroySession();
        }

        return of(initSuccess());
      }),
    ),
  );

  logout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(logout),
      exhaustMap(() =>
        this.userService.logout(false).pipe(
          map(result => {
            localStorage.removeItem('token');
            localStorage.removeItem('oldTokens');
            localStorage.removeItem('send_limit');
            localStorage.removeItem('sent_campaigns');
            return logoutSuccess();
          }),
          catchError(err => of(logoutSuccess())),
        ),
      ),
    ),
  );

  getUserSession$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getUserSession),
      exhaustMap(() =>
        this.userService.getSessionData().pipe(
          map((userData: IUserSessionData) => {
            return setUserSessionData({ data: userData });
          }),
          catchError(err => of(setUserSessionData({ data: null }))),
        ),
      ),
    ),
  );

  setUserSessionData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(setUserSessionData),
      tap(({ data }) => {
        if (data) {
          this.sessionService.loadUserSession();
        }
      }),
    ), { dispatch: false });

  toggleEmailPreview$ = createEffect(() =>
    this.actions$.pipe(
      ofType(toggleEmailPreview),
      concatMap(action =>
        this.userService
          .toggleEmailPreview(action.account_id, action.current)
          .pipe(
            take(1),
            map(() =>
              toggleEmailPreviewSuccess({
                value: !action.current,
              }),
            ),
          ),
      ),
    ),
  );

  toggleHints$ = createEffect(() =>
    this.actions$.pipe(
      ofType(toggleHints),
      switchMap(action =>
        this.userService
          .toggleHints(action.account_id, action.value)
          .pipe(map(response => toggleHintsSuccess({ value: action.value }))),
      ),
    ),
  );

  toggleUnsubscribe$ = createEffect(() =>
    this.actions$.pipe(
      ofType(toggleUnsubscribe),
      switchMap(action =>
        this.userService
          .toggleUnsubscribe(action.account_id, action.value)
          .pipe(
            map(response => {
              this.store$.dispatch(
                openSnackbar({
                  snackbarData: {
                    data: {
                      type: 'success',
                      message: `Unsubscribe feature has been ${
                        action.value ? 'Activated' : 'Deactivated'
                      }`,
                    },
                    verticalPosition: 'bottom',
                  },
                }),
              );
              return toggleUnsubscribeSuccess({ value: action.value });
            }),
          ),
      ),
    ),
  );

  toggleVipFeature$ = createEffect(() =>
    this.actions$.pipe(
      ofType(toggleVipFeature),
      concatMap(action =>
        this.userService
          .toggleVipFeature(action.account_id, action.value)
          .pipe(
            map(response => toggleVipFeatureSuccess({ value: action.value })),
          ),
      ),
    ),
  );

  constructor(
    private actions$: Actions,
    private userService: UserService,
    private sessionService: SessionService,
    private readonly store$: Store<IReducers>,
  ) {
  }
}
